import { useEffect, useMemo, useState, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowRight, Facebook, Twitter, Linkedin, Mail } from 'lucide-react'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { trackBlogViewCombined } from '../utils/analytics'
import { getImageDimensions } from '../utils/imageDimensions'
import { getCachedData, setCachedData } from '../utils/blogCache'

interface BlogPostSummary {
  id: number
  slug: string
  title: string
  excerpt: string
  category: string
  author: string
  date: string
  dateISO?: string
  readTime: string
  views?: string
  image: string
  tags?: string[]
}

interface BlogPostDetail extends BlogPostSummary {
  metaDescription?: string
  focusKeywords?: string[]
  content: string
}

const getFirstParagraph = (htmlContent: string): string => {
  const match = htmlContent.match(/<p>(.*?)<\/p>/)
  return match ? match[1].replace(/<[^>]*>/g, '') : ''
}

const removeFirstParagraph = (htmlContent: string): string => {
  return htmlContent.replace(/<p>.*?<\/p>\s*/, '')
}

const BlogDetail = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<BlogPostDetail | null>(() => {
    // Try to load from cache immediately
    if (slug) {
      const cached = getCachedData<BlogPostDetail>(`blog_detail_${slug}`)
      return cached || null
    }
    return null
  })
  const [relatedPosts, setRelatedPosts] = useState<BlogPostSummary[]>([])
  const [loading, setLoading] = useState(() => {
    // Only show loading if we don't have cached data
    return !(slug && getCachedData<BlogPostDetail>(`blog_detail_${slug}`))
  })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    let ignore = false

    const fetchData = async () => {
      // Check cache first
      const cachedPost = getCachedData<BlogPostDetail>(`blog_detail_${slug}`)
      const cachedList = getCachedData<BlogPostSummary[]>(`blog_list`)

      if (cachedPost && !ignore) {
        setPost(cachedPost)
        setLoading(false)
        
        // Get related posts from cached list
        if (cachedList) {
          const related = cachedList
            .filter((p) => p.slug !== cachedPost.slug && p.category === cachedPost.category)
            .slice(0, 3)
          setRelatedPosts(related)
        }
        
        // Track blog view
        trackBlogViewCombined(cachedPost.title, cachedPost.slug)
        
        // Still fetch in background to update cache
        fetchDataFromAPI()
        return
      }

      await fetchDataFromAPI()
    }

    const fetchDataFromAPI = async () => {
      try {
        if (!ignore) {
          setLoading(true)
          setError(null)
        }

        // Fetch detail and list in parallel
        const [detailRes, listRes] = await Promise.all([
          fetch(`/content/blog/${slug}.json`, {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            cache: 'default',
          }),
          fetch('/content/blog/list.json', {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            cache: 'default',
          }),
        ])

        if (!detailRes.ok) {
          throw new Error('İçerik bulunamadı')
        }

        const detailData: BlogPostDetail = await detailRes.json()
        let listData: BlogPostSummary[] = []
        if (listRes.ok) {
          listData = await listRes.json()
          // Cache the list
          setCachedData('blog_list', listData)
        }

        if (!ignore) {
          setPost(detailData)
          // Cache the detail
          setCachedData(`blog_detail_${slug}`, detailData)
          
          const related = listData
            .filter((p) => p.slug !== detailData.slug && p.category === detailData.category)
            .slice(0, 3)
          setRelatedPosts(related)
          
          // Track blog view in both GA4 and Meta Pixel
          trackBlogViewCombined(detailData.title, detailData.slug)
        }
      } catch (err) {
        if (!ignore) {
          setError('Yazı yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.')
        }
      } finally {
        if (!ignore) {
          setLoading(false)
        }
      }
    }

    fetchData()
    return () => {
      ignore = true
    }
  }, [slug])

  const handleBackClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    // Use navigate with state to prevent full reload
    navigate('/blog', { replace: false })
  }, [navigate])

  const metaDescription = post ? post.metaDescription || getFirstParagraph(post.content) : ''
  const keywords = post?.focusKeywords?.join(', ')
  const canonicalUrl = post ? `https://thinklyze.com/blog/${post.slug}` : 'https://thinklyze.com/blog'
  const imageUrl = post?.image ? (post.image.startsWith('http') ? post.image : `https://thinklyze.com${post.image}`) : 'https://thinklyze.com/images/logo_siyah.webp'
  const publishedDate = post?.dateISO || '2024-12-01'

  const articleSchema = useMemo(() => {
    if (!post) return null
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: metaDescription,
      image: imageUrl,
      author: {
        '@type': 'Person',
        name: post.author,
        url: 'https://thinklyze.com',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Thinklyze',
        logo: {
          '@type': 'ImageObject',
          url: 'https://thinklyze.com/images/logo_siyah.webp',
        },
      },
      mainEntityOfPage: canonicalUrl,
      datePublished: publishedDate,
      dateModified: publishedDate,
      inLanguage: 'tr-TR',
      articleSection: post.category,
      keywords: keywords || undefined,
    }
  }, [post, metaDescription, imageUrl, canonicalUrl, publishedDate, keywords])

  const breadcrumbSchema = useMemo(() => {
    if (!post) return null
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Ana Sayfa',
          item: 'https://thinklyze.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://thinklyze.com/blog',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: post.title,
          item: canonicalUrl,
        },
      ],
    }
  }, [post, canonicalUrl])

  if (loading) {
    return (
      <div className="bg-[#fafafa] min-h-screen pt-20">
        <div className="max-w-6xl mx-auto px-12 md:px-16 lg:px-20 xl:px-24 py-24 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white border border-gray-200 shadow-sm">
            <span className="w-3 h-3 rounded-full bg-blue-500 animate-ping" />
            <span className="text-sm font-medium text-gray-600">İçerik yükleniyor...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="bg-[#fafafa] min-h-screen pt-20">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Yazı bulunamadı</h1>
            <p className="text-gray-600 mb-6">{error || 'Aradığınız blog yazısı bulunamadı veya kaldırılmış olabilir.'}</p>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Blog sayfasına dön
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{post.title} - Thinklyze</title>
        <meta name="description" content={metaDescription} />
        {keywords && <meta name="keywords" content={keywords} />}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="article:published_time" content={publishedDate} />
        <meta property="article:section" content={post.category} />
        {keywords && <meta property="article:tag" content={keywords} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imageUrl} />
        {articleSchema && <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>}
        {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
      </Helmet>

      <div className="bg-[#fafafa] min-h-screen pt-14 md:pt-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20 xl:px-24 py-10 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <article className="lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-gray-200">
                    <img
                      src="/images/samed-ordueri.jpeg"
                      alt={post.author}
                      decoding="async"
                      width={getImageDimensions('/images/samed-ordueri.jpeg')?.width}
                      height={getImageDimensions('/images/samed-ordueri.jpeg')?.height}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        target.parentElement!.innerHTML = `<span class="text-white font-semibold text-sm bg-gradient-to-br from-gray-300 to-gray-400 w-full h-full flex items-center justify-center">${post.author.charAt(0)}</span>`
                      }}
                    />
                  </div>
                  <div className="text-sm text-gray-600 flex flex-wrap items-center gap-x-1 gap-y-1">
                    <span className="font-medium text-gray-900">By {post.author}</span>
                    <span className="hidden sm:inline">•</span>
                    <span>{post.category}</span>
                    <span className="hidden sm:inline">•</span>
                    <span>{post.date}</span>
                  </div>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="font-bold text-gray-900 mb-5 md:mb-6 leading-[1.12] tracking-tight text-[30px] sm:text-[34px] md:text-[42px] lg:text-[48px]"
              >
                {post.title}
              </motion.h1>

              {getFirstParagraph(post.content) && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                  className="text-base sm:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed"
                >
                  {getFirstParagraph(post.content)}
                </motion.p>
              )}

              {post.image && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mb-6 md:mb-8 rounded-xl overflow-hidden"
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    loading="lazy"
                    decoding="async"
                    width={getImageDimensions(post.image)?.width}
                    height={getImageDimensions(post.image)?.height}
                    className="w-full h-auto object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                    }}
                  />
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.25 }}
                className="article-content"
                dangerouslySetInnerHTML={{ __html: removeFirstParagraph(post.content) }}
              />

              {post.tags?.length ? (
                <div className="mt-10 md:mt-12 pt-8 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              <ShareActions title={post.title} url={canonicalUrl} />
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.1)] border border-gray-200 mt-8 lg:mt-0"
                >
                  <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-5">
                    <span className="text-white font-bold text-xl">T</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                    Thinklyze ile dijital pazarlama yolculuğunuza başlayın
                  </h3>
                  <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                    Profesyonel dijital pazarlama hizmetleri ile işinizi büyütün. Gizli ücret olmadan, saniyeler içinde başlayın, bugün kredi kartı gerektirmez.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center w-full bg-black text-white px-5 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors text-sm"
                  >
                    Ücretsiz başlayın
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </motion.div>
              </div>
            </aside>
          </div>

          {relatedPosts.length > 0 && (
            <div className="mt-20 pt-12 border-t border-gray-200">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-10">İlgili Yazılar</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow group"
                  >
                    <div className="mb-3">
                      <span className="inline-block text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-md">
                        {relatedPost.category}
                      </span>
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-900 leading-tight group-hover:text-gray-700 transition-colors mb-3 line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">{relatedPost.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

interface ShareActionsProps {
  title: string
  url: string
}

const ShareActions = ({ title, url }: ShareActionsProps) => {
  const encodedUrl = encodeURIComponent(url)
  const encodedTitle = encodeURIComponent(`${title} - Thinklyze`)
  const mailSubject = encodeURIComponent(title)
  const mailBody = encodeURIComponent(`${title}\n\n${url}`)
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  const openWindow = (shareUrl: string, target: '_blank' | '_self' = '_blank') => {
    window.open(shareUrl, target, 'noopener,noreferrer')
  }

  const shareOptions = [
    {
      label: 'Facebook',
      icon: Facebook,
      onClick: () => openWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`),
    },
    {
      label: 'Twitter',
      icon: Twitter,
      onClick: () => openWindow(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`),
    },
    {
      label: 'LinkedIn',
      icon: Linkedin,
      onClick: () => openWindow(`https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`),
    },
    {
      label: 'WhatsApp',
      icon: WhatsappIcon,
      onClick: () => openWindow(`https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`),
    },
    {
      label: 'E-posta',
      icon: Mail,
      onClick: () => (window.location.href = `mailto:?subject=${mailSubject}&body=${mailBody}`),
    },
  ]

  const handleNativeShare = async () => {
    if (canNativeShare) {
      try {
        await navigator.share({
          title,
          text: title,
          url,
        })
      } catch (err) {
        // kullanıcı iptal ederse sessiz geç
      }
    }
  }

  return (
    <div className="mt-8 pt-8 border-t border-gray-200">
      <div className="flex flex-wrap md:flex-nowrap items-center gap-2 sm:gap-3">
        {canNativeShare && (
          <button
            onClick={handleNativeShare}
            className="px-3 h-9 bg-black text-white rounded-lg flex items-center justify-center text-xs font-medium hover:bg-gray-800 transition-colors"
            type="button"
          >
            Hızlı Paylaş
          </button>
        )}
        {shareOptions.map(({ label, icon: Icon, onClick }) => (
          <button
            key={label}
            onClick={onClick}
            type="button"
            title={label}
            aria-label={label}
            className="w-9 h-9 bg-gray-100 text-gray-700 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <Icon className="w-4 h-4" />
            <span className="sr-only">{label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

interface IconProps {
  className?: string
}

const WhatsappIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    role="img"
    aria-hidden="true"
  >
    <path d="M12 2a10 10 0 0 0-8.59 15.17L2 22l4.94-1.3A10 10 0 1 0 12 2Zm3.83 13.88c-.19.54-1.1 1-1.53 1s-.82.27-2.78-.57a9.67 9.67 0 0 1-4.52-3.94c-1.85-3.2-.46-3.55.31-4.73.16-.25.67-.5.89-.5s.44 0 .64.01c.21.01.49-.08.76.58.27.66.9 2.26.98 2.43.08.17.14.37.03.6-.11.22-.17.35-.32.53-.16.18-.34.4-.15.77s.85 1.4 1.82 2.27c1.25 1.12 1.82 1.18 2.18 1.06.36-.11.57-.48.73-.65.16-.17.32-.14.54-.08.22.07 1.4.66 1.64.77.24.11.4.17.46.27.06.11.06.63-.12 1.17Z" />
  </svg>
)

export default BlogDetail




